import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const EN = {
  "brand.name": "VERALIX AEGIS",
  "brand.tagline": "Sovereign Digital Evidence · Zero-Trust Governance",
  "shell.officer": "Duty Officer",
  "shell.signout": "Sign out",
  "shell.locked": "Grid isolated",
  "shell.console": "Console",
  "nav.group.access": "Access",
  "nav.group.ops": "Operations",
  "nav.group.assurance": "Assurance",
  "nav.group.advanced": "Advanced",
  "nav.group.cases": "Case files",
  "nav.matter": "Matter Workspace",
  "nav.auth": "Auth Portal",
  "nav.source": "Connect First Source",
  "nav.exec": "Executive OS",
  "nav.intake": "Intake & Seal",
  "nav.ai": "AI Workspace",
  "nav.custody": "Custody Ledger",
  "nav.audit": "Audit Timeline",
  "nav.exception": "Exception",
  "nav.policy": "Policy Studio",
  "nav.deploy": "Delivery Package",
  "nav.gallery": "Design Gallery",
  "gate.step1": "Terminal gate",
  "exp.failed": "Export failed — the briefing engine did not respond in time. Please try again.",
  "gate.step2": "Hardware key",
  "gate.title": "This terminal is sealed.",
  "gate.sub": "Zero trust means no back doors. Every screen of the console stays locked until the organisational passcode is verified server-side — then your hardware key signs you in as the officer of record.",
  "gate.card": "Perimeter passcode gate",
  "gate.label": "Organisational passcode",
  "gate.cta": "Unlock terminal",
  "gate.busy": "Verifying…",
  "gate.note": "Verified server-side against a bcrypt hash — the passcode never reaches this browser. {max} wrong attempts locks this IP for {mins} minutes. Every attempt, pass or fail, is written to the WORM ledger; the passcode itself is never logged.",
  "gate.invalid": "Passcode rejected. Attempt recorded in the WORM ledger.",
  "gate.attempts": "{n} attempts left before lockout",
  "gate.locked_out": "Too many failed attempts. This terminal is locked out.",
  "gate.lock_timer": "Locked out — retry in {s}s",
  "gate.state.sealed": "TERMINAL SEALED · AWAITING PASSCODE",
  "gate.passed": "Gate cleared · WORM #{n}",
  "gate.relock": "re-lock",
  "gate.inventory": "Console screens · locked",
  "gate.need_gate": "{n} is locked. Clear the passcode gate first.",
  "gate.need_key": "{n} needs your hardware key signature.",
  "gate.lost": "Terminal session expired — the console has re-sealed itself.",
  "theme.toggle": "Switch theme / 切换主题",
  "theme.dark": "Matte dark",
  "theme.light": "Matte day",
  "shell.print": "Print current view",
  "gal.screen": "Screen",
  "gal.title": "Design Concept Gallery",
  "gal.sub": "High-fidelity concept boards for screens 03–08, kept in-product so leadership can walk the whole doctrine without a slide deck.",
  "gal.boards": "Concept boards",
  "gal.count": "{n} boards · screens 03–08 reference set",
  "gal.hint": "Click any board to enlarge · ← → to move between boards · Esc to close",
  "gal.present": "Presentation mode",
  "gal.auto": "Auto-advance",
  "gal.exit": "Close",
  "gal.c03.title": "Executive OS · Decision Cockpit",
  "gal.c03.cap": "Court-readiness gauges, intercept KPIs and the rolling world map on one matte surface — the single screen a duty officer watches.",
  "gal.c04.title": "Intake & Seal · Evidence Custody",
  "gal.c04.cap": "Drop a file, watch the SHA-256 fingerprint computed in the browser, confirm the server digest matches, then seal it into the WORM chain.",
  "gal.c05.title": "AI Workspace · Artifact Clustering",
  "gal.c05.cap": "The copilot transcript beside a live clustering canvas, with one-click Word and Chinese PDF export of the forensic briefing.",
  "gal.c06.title": "Custody Ledger · Hash Chain",
  "gal.c06.cap": "Every block with its own and its predecessor's digest, plus the tamper drill that proves a single edited byte breaks the chain.",
  "gal.c07.title": "Audit Timeline · Immutable Record",
  "gal.c07.cap": "Who acted, on what, at which UTC second — a chronological rail that can be handed to an auditor as-is.",
  "gal.c08.title": "Exception Center · Human In The Loop",
  "gal.c08.cap": "Nothing high-risk proceeds silently: every impossible-travel case waits here for a hardware-key signature.",

  "auth.overline": "Screen 01 · Auth Portal",
  "auth.title": "Hardware-Key Step-Up Authentication",
  "auth.sub": "Insert your Veralix hardware key and confirm with the officer PIN. Every entry is anchored to the WORM ledger.",
  "auth.key": "Hardware key",
  "auth.state.idle": "Awaiting key presence",
  "auth.state.reading": "Reading attestation…",
  "auth.state.verified": "Assertion verified",
  "auth.state.denied": "Assertion rejected",
  "auth.pin": "Officer PIN (demo default 123456)",
  "auth.cta": "Authenticate & enter console",
  "auth.busy": "Verifying assertion…",
  "auth.error": "PIN rejected — hardware assertion failed",
  "auth.ok": "Welcome back, officer",
  "auth.f1": "FIDO2 / WebAuthn public-key assertion",
  "auth.f2": "HMAC-SHA256 signature over intent digest",
  "auth.f3": "Every login sealed into the audit ledger",
  "auth.note": "Simulated hardware attestation for this preview environment.",

  "src.overline": "Screen 02 · Connect First Source",
  "src.title": "Bind Your First Evidence Source",
  "src.sub": "Pick one source to begin. Everything is attached read-only — nothing at the source is ever modified.",
  "src.connect": "Connect",
  "src.connecting": "Binding…",
  "src.connected": "Connected",
  "src.records": "records",
  "src.continue": "Continue to command centre",
  "src.progress": "{n} of {total} sources bound",
  "src.protocol": "Transport",
  "src.readonly": "Read-only",

  "exec.overline": "Screen 03 · Executive OS",
  "exec.title": "Command Centre",
  "exec.sub": "One screen, three buttons. No technical background required.",
  "exec.g.readiness": "Court readiness",
  "exec.g.integrity": "Ledger integrity",
  "exec.g.custody": "Custody sealed",
  "exec.kpi.blocked": "Intercepted actions",
  "exec.kpi.pending": "Awaiting signature",
  "exec.kpi.height": "Ledger height",
  "exec.kpi.sources": "Bound sources",
  "exec.one.title": "One-Click Console",
  "exec.one.sub": "Each button completes a full custody procedure and seals the result.",
  "exec.btn.seal": "Seal all evidence",
  "exec.btn.report": "Generate briefing",
  "exec.btn.preserve": "Preserve scene state",
  "exec.btn.seal.hint": "Locks every unsealed artifact into custody",
  "exec.btn.report.hint": "Plain-language brief for decision makers",
  "exec.btn.preserve.hint": "Freezes a signed snapshot of the whole case",
  "exec.pending.title": "Awaiting Steward signature",
  "exec.pending.sub": "Step-up approval gate · FIDO2",
  "exec.pending.empty": "No pending step-up actions. The grid is clear.",
  "exec.sign": "Verify PIN & sign",
  "exec.simulate": "Simulate high-risk action",
  "exec.lockdown": "Red-alert isolation",
  "exec.heartbeat": "Console heartbeat",
  "exec.snapshot.latest": "Latest preserved snapshot",

  "seal.overline": "Screen 04 · Intake & Seal",
  "seal.title": "Evidence Intake & Custody Seal",
  "seal.sub": "Drop an artifact, read the plain-language verdict, then seal it with one click.",
  "seal.drop": "Click or drop a log / image here to start forensic intake",
  "seal.hint": ".log · .json · .pcap · .png · .jpg — SHA-256 bound on arrival",
  "seal.analyzing": "Analysing in the isolated sandbox…",
  "seal.report": "Assessment report",
  "seal.threat": "Threat score",
  "seal.digest": "SHA-256 digest",
  "seal.chain": "Evidence chain",
  "seal.recommend": "Recommended",
  "seal.btn": "One-click seal to custody",
  "seal.sealing": "Sealing…",
  "seal.sealed": "Sealed · block #{n}",
  "seal.list": "Ingested artifacts",
  "seal.list.empty": "No artifacts yet.",
  "seal.unsealed": "Unsealed",
  "seal.none": "Nothing left to seal — every artifact is already in custody.",
  "seal.matter": "Matter selector",
  "seal.matter.hint": "Evidence is filed under the selected matter and its legal hold.",
  "seal.clientHash": "Live SHA-256 (computed in your browser)",
  "seal.hashing": "Hashing locally…",
  "seal.match": "Client / server digest match",
  "seal.mismatch": "Digest mismatch — do not seal",
  "seal.legalhold": "Legal hold",
  "seal.evidenceCount": "{n} artifacts · {s} sealed",

  "exp.title": "Judicial report export",
  "exp.sub": "Builds a real .docx and .pdf file in your browser from the sealed ledger — nothing is uploaded.",
  "exp.docx": "Export Word (.docx) report",
  "exp.pdf": "Export PDF (.pdf) attestation",
  "exp.building": "Compiling document…",
  "exp.done": "{f} downloaded",
  "exp.needReport": "Drafting the briefing first…",
  "exp.note": "The Word edition is bilingual; the PDF edition is English (embedded fonts).",
  "exp.pdfzh": "Export Chinese PDF (server-sealed)",

  "drill.title": "Ledger tamper drill",
  "drill.sub": "Prove the chain is tamper-evident — safely, and reversibly.",
  "drill.body": "The drill overwrites one block's payload digest out-of-band, exactly like an intruder editing the database directly. Run the chain verification afterwards: the broken link is flagged instantly and an exception is raised. Restore puts the original digest back.",
  "drill.start": "Corrupt a block (signed)",
  "drill.restore": "Restore ledger",
  "drill.active": "Drill active — block #{n} digest forged",
  "drill.idle": "No drill active. The ledger is in its authentic state.",
  "drill.started": "Drill active — block #{n} corrupted. Run verification.",
  "drill.restored": "Ledger restored — block(s) {b}",

  "mw.overline": "Screen 09 · Matter Workspace",
  "mw.title": "Matter Workspace",
  "mw.sub": "Everything filed under one case: artifacts, custody attestations and issued reports.",
  "mw.loading": "Loading matter file…",
  "mw.authority": "Issuing authority",
  "mw.officer": "Lead officer",
  "mw.clearance": "Clearance / status",
  "mw.opened": "Opened",
  "mw.evidence": "Artifacts",
  "mw.sealed": "Sealed",
  "mw.attestations": "Attestations",
  "mw.maxthreat": "Peak threat",
  "mw.generate": "Generate briefing for this matter",
  "mw.exports": "Open export console",
  "mw.custodyScore": "Custody completeness",
  "mw.custodyScoreSub": "Share of this matter's artifacts under seal",
  "mw.sealedRatio": "Sealed ratio",
  "mw.evidenceList": "Artifacts in this matter",
  "mw.noEvidence": "No artifacts filed under this matter yet.",
  "mw.custodyLog": "Custody attestation log",
  "mw.custodyLogSub": "Every seal written to the WORM log",
  "mw.noCustody": "No seal attestations recorded yet.",
  "mw.exportHistory": "Issued reports",
  "mw.exportHistorySub": "Briefings generated for this matter",
  "mw.noReports": "No briefing issued for this matter yet.",

  "act.title": "Copilot actions",
  "act.sub": "Turn an intent into a Rego policy or a step-up approval, then hand it straight over.",
  "act.intent": "Officer intent",
  "act.placeholder": "e.g. block privileged escalation unless the FIDO2 assertion is under 120s old",
  "act.draftPolicy": "Draft Rego policy",
  "act.draftApproval": "Draft step-up approval",
  "act.drafting": "Drafting…",
  "act.drafted": "draft ready",
  "act.needIntent": "Describe the intent in a few words first.",
  "act.failed": "The drafting engine could not complete that.",
  "act.sendStudio": "Hand off to Policy Studio",
  "act.sendGate": "Queue at the approval gate",
  "act.toStudio": "{id} loaded into the Policy Studio.",
  "act.toGate": "{id} queued at the step-up gate.",
  "act.disposition": "Recommended disposition",
  "act.note": "Drafts are advisory only — nothing is enforced until you hand it off and a Steward signs.",
  "act.incoming": "Drafted by the Copilot",

  "tm.title": "Live threat map",
  "tm.sub": "Rolling geo-IP interceptions feeding the impossible-travel detector.",
  "tm.live": "LIVE SENTINEL",
  "tm.inject": "Inject impossible travel",
  "tm.loading": "Acquiring geo telemetry…",
  "tm.detector": "Impossible-travel detector",
  "tm.threshold": "threshold {n} km/h",
  "tm.noAlert": "No impossible-travel pattern in the current window.",
  "tm.feed": "Interception feed",
  "tm.regions": "{n} regions · {i} identities",
  "tm.alert": "IMPOSSIBLE TRAVEL — {a} → {b} at ~{s} km/h. Session quarantined.",
  "tm.v.ALLOWED": "Allowed",
  "tm.v.STEP_UP": "Step-up",
  "tm.v.BLOCKED": "Blocked",
  "tm.v.IMPOSSIBLE_TRAVEL": "Impossible travel",

  "vfy.overline": "Public ledger verification",
  "vfy.public": "Public WORM block verification · no sign-in required",
  "vfy.title": "WORM block #{n}",
  "vfy.sub": "This page independently re-computes the block hash and checks the forward link in the chain.",
  "vfy.loading": "Re-computing hashes…",
  "vfy.ok": "Block verified — hash re-computed and forward link intact.",
  "vfy.bad": "Integrity failure — this block does not match its hash chain.",
  "vfy.notfound": "Block not found in the ledger.",
  "vfy.height": "chain top #{n}",
  "vfy.count": "{n} sealed blocks",
  "vfy.action": "Action",
  "vfy.time": "Timestamp",
  "vfy.signer": "Steward signer",
  "vfy.hash": "Block hash",
  "vfy.prev": "Previous hash",
  "vfy.selfhash": "Self hash",
  "vfy.forward": "Forward link",
  "vfy.payload": "Sealed payload",
  "vfy.console": "Open the Aegis console",

  "ai.overline": "Screen 05 · AI Workspace",
  "ai.title": "AI Investigation Workspace",
  "ai.sub": "Read-only inference plane. The model advises — it never acts on your case data.",
  "ai.cluster": "Artifact clustering visualization",
  "ai.cluster.sub": "Live force-directed clustering of every artifact and ledger event",
  "ai.cluster.empty": "Bind a source or ingest an artifact to populate the cluster field.",
  "ai.chat": "Aegis Copilot",
  "ai.status": "Live · advisory only",
  "ai.placeholder": "Ask the Aegis Copilot…",
  "ai.nodes": "nodes",
  "ai.clusters": "clusters",

  "cust.overline": "Screen 06 · Custody Ledger",
  "cust.title": "Chain-of-Custody Ledger",
  "cust.sub": "Every action appends a block. Each block hashes the one before it.",
  "cust.verify": "Verify whole chain",
  "cust.verifying": "Verifying…",
  "cust.export": "Export JSON",
  "cust.ok": "Chain verified — {n} blocks, zero broken links.",
  "cust.bad": "Integrity breach at block(s): {b}",
  "cust.blocks": "Hash-chained blocks",
  "cust.newest": "{n} sealed blocks · newest first",
  "cust.signer": "Signer",

  "audit.overline": "Screen 07 · Audit Timeline",
  "audit.title": "Audit Timeline",
  "audit.sub": "A continuous, human-readable record of everything the platform did.",
  "audit.all": "All",
  "audit.success": "Success",
  "audit.blocked": "Blocked",
  "audit.standard": "Routine",
  "audit.warning": "Warning",
  "audit.empty": "No events for this filter.",
  "audit.export": "Export timeline",
  "audit.events": "{n} events",

  "exc.overline": "Screen 08 · Exception",
  "exc.title": "Exception Handling",
  "exc.sub": "Anything that needs a human decision lands here — nothing is silently auto-approved.",
  "exc.empty": "No open exceptions. Every intent is disposed.",
  "exc.resolve": "Dispose & sign",
  "exc.remediation": "Remediation",
  "exc.target": "Target",
  "exc.detected": "Detected",
  "exc.lockdown.title": "Red-alert isolation",
  "exc.lockdown.on": "Engage isolation",
  "exc.lockdown.off": "Lift isolation",
  "exc.lockdown.sub": "Freezes every external write action across the grid.",
  "exc.open": "{n} open",

  "rep.title": "Executive briefing",
  "rep.generating": "Drafting the briefing…",
  "rep.summary": "Executive summary",
  "rep.integrity": "Integrity statement",
  "rep.findings": "Key findings",
  "rep.actions": "Recommended actions",
  "rep.posture": "Risk posture",
  "rep.download": "Download JSON",
  "rep.close": "Close",
  "rep.sealed": "Sealed to WORM block #{n}",

  "steward.title": "Steward step-up approval",
  "steward.sub": "Bind the challenge and append an HMAC attestation",
  "steward.request": "Request",
  "steward.action": "Action",
  "steward.pin": "Steward PIN (demo default 123456)",
  "steward.cancel": "Cancel",
  "steward.sign": "Sign attestation",
  "steward.signing": "Signing…",
  "steward.anchor": "HMAC-SHA256 anchored to WORM",
};

const ZH = {
  "brand.name": "VERALIX AEGIS",
  "brand.tagline": "主权级数字证据 · 零信任治理平台",
  "shell.officer": "值班官员",
  "shell.signout": "退出登录",
  "shell.locked": "网格已隔离",
  "shell.console": "控制台",
  "nav.group.access": "接入",
  "nav.group.ops": "运行",
  "nav.group.assurance": "保障",
  "nav.group.advanced": "高级",
  "nav.group.cases": "案件卷宗",
  "nav.matter": "案件工作台",
  "nav.auth": "身份门户",
  "nav.source": "接入首个数据源",
  "nav.exec": "决策指挥中枢",
  "nav.intake": "证据接入与封存",
  "nav.ai": "AI 工作台",
  "nav.custody": "保管链账本",
  "nav.audit": "审计时间轴",
  "nav.exception": "异常处置",
  "nav.policy": "策略规约",
  "nav.deploy": "生产交付包",
  "nav.gallery": "设计概念画廊",
  "gate.step1": "通行码闸门",
  "exp.failed": "导出失败——简报引擎未在规定时间内响应，请重试。",
  "gate.step2": "硬件密钥",
  "gate.title": "本终端已封锁。",
  "gate.sub": "零信任意味着没有后门。在机构通行码通过服务端校验之前，控制台的每一个画面都保持锁定；通过之后，再由你的硬件密钥完成办案人身份签名。",
  "gate.card": "边界通行码闸门",
  "gate.label": "机构通行码",
  "gate.cta": "解锁终端",
  "gate.busy": "正在校验…",
  "gate.note": "通行码仅在服务端与 bcrypt 哈希比对，明文永不下发到浏览器。同一 IP 连续输错 {max} 次将锁定 {mins} 分钟。每一次尝试（成功或失败）都会写入 WORM 账本，但绝不记录通行码本身。",
  "gate.invalid": "通行码被拒绝，本次尝试已写入 WORM 账本。",
  "gate.attempts": "锁定前还剩 {n} 次机会",
  "gate.locked_out": "失败次数过多，本终端已被锁定。",
  "gate.lock_timer": "已锁定 — {s} 秒后可重试",
  "gate.state.sealed": "终端已封锁 · 等待通行码",
  "gate.passed": "闸门已通过 · WORM #{n}",
  "gate.relock": "重新上锁",
  "gate.inventory": "控制台画面 · 锁定中",
  "gate.need_gate": "{n} 已锁定，请先通过通行码闸门。",
  "gate.need_key": "{n} 还需要你的硬件密钥签名。",
  "gate.lost": "终端会话已过期，控制台已重新封锁。",
  "theme.toggle": "切换主题 / Switch theme",
  "theme.dark": "哑光深色",
  "theme.light": "哑光日间",
  "shell.print": "打印当前视图",
  "gal.screen": "画面",
  "gal.title": "设计概念画廊",
  "gal.sub": "03–08 屏高保真概念图，内置于系统中，便于向领导完整讲清整套办案主线，无需另做幻灯片。",
  "gal.boards": "概念图板",
  "gal.count": "共 {n} 张 · 03–08 屏基准视觉",
  "gal.hint": "点击任意概念图放大 · ← → 切换 · Esc 关闭",
  "gal.present": "演示模式",
  "gal.auto": "自动播放",
  "gal.exit": "关闭",
  "gal.c03.title": "决策指挥中枢 · 一屏定局",
  "gal.c03.cap": "出庭就绪度仪表、拦截指标与滚动世界地图集中在同一块哑光面板上——值班官员只需看这一屏。",
  "gal.c04.title": "证据接入与封存 · 保管起点",
  "gal.c04.cap": "拖入文件即在浏览器本地算出 SHA-256 指纹，与服务端指纹核对一致后，一键封存进 WORM 哈希链。",
  "gal.c05.title": "AI 工作台 · 物证聚类",
  "gal.c05.cap": "AI 对话记录与实时聚类画布并排呈现，一键导出 Word 与中文 PDF 鉴定报告。",
  "gal.c06.title": "保管链账本 · 哈希链",
  "gal.c06.cap": "每个区块同时展示自身与前序摘要，并提供篡改演练：改动一个字节即可让整条链断裂可证。",
  "gal.c07.title": "审计时间轴 · 不可篡改留痕",
  "gal.c07.cap": "谁、在哪一 UTC 秒、对什么做了什么——一条可直接交给审计人员的时间轴。",
  "gal.c08.title": "异常处置中心 · 人工在环",
  "gal.c08.cap": "凡高风险动作绝不静默放行：每起不可能旅行事件都在此等待硬件密钥签名。",

  "auth.overline": "画面 01 · 身份门户",
  "auth.title": "硬件密钥步进认证",
  "auth.sub": "插入 Veralix 硬件密钥并输入官员 PIN。每一次登录都会封存进 WORM 账本。",
  "auth.key": "硬件密钥",
  "auth.state.idle": "等待密钥插入",
  "auth.state.reading": "正在读取密钥认证…",
  "auth.state.verified": "断言校验通过",
  "auth.state.denied": "断言被拒绝",
  "auth.pin": "官员 PIN（演示默认 123456）",
  "auth.cta": "认证并进入控制台",
  "auth.busy": "正在校验断言…",
  "auth.error": "PIN 校验失败 — 硬件断言被拒绝",
  "auth.ok": "欢迎回来，官员",
  "auth.f1": "FIDO2 / WebAuthn 公钥断言",
  "auth.f2": "对意图摘要执行 HMAC-SHA256 签名",
  "auth.f3": "每次登录均封存进审计账本",
  "auth.note": "本预览环境使用模拟硬件认证流程。",

  "src.overline": "画面 02 · 接入首个数据源",
  "src.title": "绑定第一个证据来源",
  "src.sub": "先选一个来源即可开始。全部以只读方式接入，绝不改动源端任何数据。",
  "src.connect": "接入",
  "src.connecting": "正在绑定…",
  "src.connected": "已接入",
  "src.records": "条记录",
  "src.continue": "进入指挥中枢",
  "src.progress": "已绑定 {n} / {total} 个数据源",
  "src.protocol": "传输方式",
  "src.readonly": "只读",

  "exec.overline": "画面 03 · 决策指挥中枢",
  "exec.title": "指挥中枢",
  "exec.sub": "一屏三键，无需任何技术背景即可完成处置。",
  "exec.g.readiness": "出证就绪度",
  "exec.g.integrity": "账本完整度",
  "exec.g.custody": "已封存比例",
  "exec.kpi.blocked": "已拦截高危动作",
  "exec.kpi.pending": "待签名动作",
  "exec.kpi.height": "账本高度",
  "exec.kpi.sources": "已绑定来源",
  "exec.one.title": "一键处置台",
  "exec.one.sub": "每个按钮都会完整走完保管流程，并将结果封存上链。",
  "exec.btn.seal": "一键封存全部证据",
  "exec.btn.report": "一键生成呈报报告",
  "exec.btn.preserve": "一键固定现场状态",
  "exec.btn.seal.hint": "把所有未封存物证锁入保管链",
  "exec.btn.report.hint": "为决策者生成通俗易懂的简报",
  "exec.btn.preserve.hint": "对全案状态生成带签名的快照",
  "exec.pending.title": "待主理官签名动作",
  "exec.pending.sub": "步进审批闸门 · FIDO2",
  "exec.pending.empty": "当前没有待签名动作，态势清朗。",
  "exec.sign": "验证 PIN 并签名",
  "exec.simulate": "模拟高危动作",
  "exec.lockdown": "一键红警隔离",
  "exec.heartbeat": "控制台心跳流水",
  "exec.snapshot.latest": "最近一次现场快照",

  "seal.overline": "画面 04 · 证据接入与封存",
  "seal.title": "证据接入与保管封存",
  "seal.sub": "拖入物证，阅读通俗结论，然后一键封存。",
  "seal.drop": "点击或拖入日志 / 图片，启动取证接入",
  "seal.hint": ".log · .json · .pcap · .png · .jpg — 到达即绑定 SHA-256",
  "seal.analyzing": "正在隔离沙箱中解析…",
  "seal.report": "研判报告",
  "seal.threat": "威胁评分",
  "seal.digest": "SHA-256 摘要",
  "seal.chain": "证据链条",
  "seal.recommend": "处置建议",
  "seal.btn": "一键封存至保管链",
  "seal.sealing": "正在封存…",
  "seal.sealed": "已封存 · 区块 #{n}",
  "seal.list": "已接入物证",
  "seal.list.empty": "暂无物证。",
  "seal.unsealed": "未封存",
  "seal.none": "已无待封存物证 — 全部物证均已在保管链内。",
  "seal.matter": "归属案件",
  "seal.matter.hint": "物证将登记在所选案件及其法定保全之下。",
  "seal.clientHash": "现场 SHA-256（浏览器本地计算）",
  "seal.hashing": "正在本地计算指纹…",
  "seal.match": "本地与服务端指纹一致",
  "seal.mismatch": "指纹不一致 — 请勿封存",
  "seal.legalhold": "法定保全",
  "seal.evidenceCount": "{n} 件物证 · 已封存 {s} 件",

  "exp.title": "司法报告导出",
  "exp.sub": "在浏览器本地基于已封存账本生成真实的 .docx 与 .pdf 文件，不上传任何数据。",
  "exp.docx": "导出 Word (.docx) 办案报告",
  "exp.pdf": "导出 PDF (.pdf) 司法存证",
  "exp.building": "正在编排文档…",
  "exp.done": "{f} 已下载",
  "exp.needReport": "正在先生成简报…",
  "exp.note": "Word 版为中英双语；PDF 版为英文（受内置字体限制）。",
  "exp.pdfzh": "导出中文 PDF（服务端盖章版）",

  "drill.title": "账本篡改演示",
  "drill.sub": "在可控、可复原的前提下，验证账本确实防篡改。",
  "drill.body": "演示会绕过应用层直接改写某个区块的载荷摘要，完全模拟入侵者篡改数据库的手法。随后运行全链校验，断链会被立即标红并在异常中心生成事件；点击「复原账本」即可还原原始摘要。",
  "drill.start": "签名后篡改一个区块",
  "drill.restore": "复原账本",
  "drill.active": "演示进行中 — 区块 #{n} 摘要已被伪造",
  "drill.idle": "当前无演示，账本处于真实状态。",
  "drill.started": "演示已启动 — 区块 #{n} 已被篡改，请运行全链校验。",
  "drill.restored": "账本已复原 — 区块 {b}",

  "mw.overline": "画面 09 · 案件工作台",
  "mw.title": "案件工作台",
  "mw.sub": "一个案件的全部内容：物证、保管链存证与已出具报告。",
  "mw.loading": "正在载入卷宗…",
  "mw.authority": "出具机构",
  "mw.officer": "主办侦查员",
  "mw.clearance": "权限密级 / 状态",
  "mw.opened": "立案时间",
  "mw.evidence": "物证数",
  "mw.sealed": "已封存",
  "mw.attestations": "存证条数",
  "mw.maxthreat": "最高威胁分",
  "mw.generate": "为本案生成呈报简报",
  "mw.exports": "打开导出台",
  "mw.custodyScore": "保管完整度",
  "mw.custodyScoreSub": "本案物证的封存比例",
  "mw.sealedRatio": "封存比例",
  "mw.evidenceList": "本案物证",
  "mw.noEvidence": "本案暂无登记物证。",
  "mw.custodyLog": "保管链存证流水",
  "mw.custodyLogSub": "写入 WORM 日志的每一次封存",
  "mw.noCustody": "暂无封存存证记录。",
  "mw.exportHistory": "已出具报告",
  "mw.exportHistorySub": "本案历史简报与导出记录",
  "mw.noReports": "本案暂未出具报告。",

  "act.title": "助手可执行动作",
  "act.sub": "把一句意图转成 Rego 策略或步进审批，并直接移交下游。",
  "act.intent": "官员意图",
  "act.placeholder": "例如：断言超过 120 秒即禁止特权提权",
  "act.draftPolicy": "起草 Rego 策略",
  "act.draftApproval": "起草步进审批",
  "act.drafting": "起草中…",
  "act.drafted": "草案已生成",
  "act.needIntent": "请先用几个字描述你的意图。",
  "act.failed": "起草引擎未能完成本次请求。",
  "act.sendStudio": "移交策略规约",
  "act.sendGate": "送入审批闸门",
  "act.toStudio": "{id} 已载入策略规约编辑器。",
  "act.toGate": "{id} 已进入步进审批闸门。",
  "act.disposition": "建议处置",
  "act.note": "草案仅为建议 —— 移交并由主理官签名后才会真正生效。",
  "act.incoming": "由助手起草",

  "tm.title": "实时威胁地图",
  "tm.sub": "滚动汇聚被拦截的异地登录，驱动不可能旅行检测器。",
  "tm.live": "哨兵在线",
  "tm.inject": "注入不可能旅行",
  "tm.loading": "正在获取地理遥测…",
  "tm.detector": "不可能旅行检测器",
  "tm.threshold": "阈值 {n} 公里/小时",
  "tm.noAlert": "当前窗口未发现不可能旅行特征。",
  "tm.feed": "拦截事件流",
  "tm.regions": "{n} 个区域 · {i} 个身份",
  "tm.alert": "不可能旅行 —— {a} → {b}，折算时速约 {s} 公里/小时，会话已隔离。",
  "tm.v.ALLOWED": "放行",
  "tm.v.STEP_UP": "需步进",
  "tm.v.BLOCKED": "已拦截",
  "tm.v.IMPOSSIBLE_TRAVEL": "不可能旅行",

  "vfy.overline": "公开账本验证",
  "vfy.public": "WORM 区块公开验证 · 无需登录",
  "vfy.title": "WORM 区块 #{n}",
  "vfy.sub": "本页独立重算该区块哈希，并校验其在链上的前后链接关系。",
  "vfy.loading": "正在重算哈希…",
  "vfy.ok": "区块校验通过 — 哈希重算一致，前后链接完整。",
  "vfy.bad": "完整性校验失败 — 该区块与哈希链不匹配。",
  "vfy.notfound": "账本中未找到该区块。",
  "vfy.height": "链顶高度 #{n}",
  "vfy.count": "已封存 {n} 个区块",
  "vfy.action": "动作类型",
  "vfy.time": "时间戳",
  "vfy.signer": "签名人",
  "vfy.hash": "区块哈希",
  "vfy.prev": "前序哈希",
  "vfy.selfhash": "自哈希校验",
  "vfy.forward": "前向链接",
  "vfy.payload": "已封存载荷",
  "vfy.console": "进入 Aegis 控制台",

  "ai.overline": "画面 05 · AI 工作台",
  "ai.title": "AI 侦查工作台",
  "ai.sub": "只读推理平面。模型只提供建议，绝不对案件数据执行写操作。",
  "ai.cluster": "物证聚类可视化",
  "ai.cluster.sub": "对全部物证与账本事件进行实时力导向聚类",
  "ai.cluster.empty": "请先绑定数据源或接入物证以生成聚类图。",
  "ai.chat": "Aegis 智能助手",
  "ai.status": "实时 · 仅提供建议",
  "ai.placeholder": "向 Aegis 智能助手提问…",
  "ai.nodes": "节点",
  "ai.clusters": "聚类",

  "cust.overline": "画面 06 · 保管链账本",
  "cust.title": "证据保管链账本",
  "cust.sub": "每个动作都会追加一个区块，每个区块都对前一区块取哈希。",
  "cust.verify": "校验全链完整性",
  "cust.verifying": "校验中…",
  "cust.export": "导出 JSON",
  "cust.ok": "全链完整 — 共 {n} 个区块，零断链。",
  "cust.bad": "以下区块存在完整性破损：{b}",
  "cust.blocks": "哈希链区块",
  "cust.newest": "共 {n} 个已封存区块 · 最新在前",
  "cust.signer": "签名人",

  "audit.overline": "画面 07 · 审计时间轴",
  "audit.title": "审计时间轴",
  "audit.sub": "平台所有动作的连续、可读记录。",
  "audit.all": "全部",
  "audit.success": "成功",
  "audit.blocked": "拦截",
  "audit.standard": "常规",
  "audit.warning": "告警",
  "audit.empty": "该筛选条件下暂无事件。",
  "audit.export": "导出时间轴",
  "audit.events": "共 {n} 条事件",

  "exc.overline": "画面 08 · 异常处置",
  "exc.title": "异常处置中心",
  "exc.sub": "凡需人工决断的事项都会汇聚到这里，绝不静默自动放行。",
  "exc.empty": "当前没有待处置异常，全部意图均已处置。",
  "exc.resolve": "处置并签名",
  "exc.remediation": "处置建议",
  "exc.target": "作用对象",
  "exc.detected": "发现时间",
  "exc.lockdown.title": "红警隔离",
  "exc.lockdown.on": "启动隔离",
  "exc.lockdown.off": "解除隔离",
  "exc.lockdown.sub": "冻结全网所有外部写操作。",
  "exc.open": "{n} 项待处置",

  "rep.title": "呈报简报",
  "rep.generating": "正在起草简报…",
  "rep.summary": "决策摘要",
  "rep.integrity": "完整性声明",
  "rep.findings": "关键要点",
  "rep.actions": "建议措施",
  "rep.posture": "风险态势",
  "rep.download": "下载 JSON",
  "rep.close": "关闭",
  "rep.sealed": "已封存至 WORM 区块 #{n}",

  "steward.title": "主理官步进审批",
  "steward.sub": "绑定质询算法并追加 HMAC 存证",
  "steward.request": "请求编号",
  "steward.action": "动作类型",
  "steward.pin": "主理官 PIN（演示默认 123456）",
  "steward.cancel": "取消",
  "steward.sign": "生成 HMAC 存证",
  "steward.signing": "签名中…",
  "steward.anchor": "HMAC-SHA256 锚定至 WORM",
};

const DICTS = { en: EN, zh: ZH };
const Ctx = createContext({ lang: "zh", t: (k) => k, toggle: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => window.localStorage.getItem("aegis.lang") || "zh");

  const toggle = useCallback(() => {
    setLang((l) => {
      const next = l === "zh" ? "en" : "zh";
      window.localStorage.setItem("aegis.lang", next);
      return next;
    });
  }, []);

  const t = useCallback(
    (key, vars) => {
      let s = DICTS[lang][key] ?? EN[key] ?? key;
      if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
      return s;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, t, toggle }), [lang, t, toggle]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useI18n = () => useContext(Ctx);

export const pickLang = (obj, base, lang) => obj?.[`${base}_${lang}`] ?? obj?.[`${base}_en`] ?? "";
